import { format, subDays } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

export function defaultReportRange(days = 30): { date_from: string; date_to: string } {
  const to = new Date();
  const from = subDays(to, days);
  return {
    date_from: format(from, 'yyyy-MM-dd'),
    date_to: format(to, 'yyyy-MM-dd'),
  };
}

export function downloadCsv(filename: string, rows: (string | number | null | undefined)[][]) {
  const esc = (v: string | number | null | undefined) => {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const bom = '\uFEFF';
  const body = rows.map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([bom + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function printElementById(elementId: string) {
  const el = document.getElementById(elementId);
  if (!el) {
    window.print();
    return;
  }
  const w = window.open('', '_blank', 'noopener,noreferrer,width=1024,height=768');
  if (!w) {
    window.print();
    return;
  }
  w.document.write(
    `<!DOCTYPE html><html><head><title>리포트 인쇄</title>
    <style>
      body { font-family: 'Malgun Gothic', sans-serif; padding: 16px; color: #111; }
      table { border-collapse: collapse; width: 100%; margin: 12px 0; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; }
      th { background: #f5f5f5; }
      h1,h2,h3 { margin: 8px 0; }
      .MuiCard-root, .MuiPaper-root { box-shadow: none !important; border: 1px solid #ddd; margin-bottom: 12px; }
      @media print { button { display: none !important; } }
    </style></head><body>${el.innerHTML}</body></html>`
  );
  w.document.close();
  setTimeout(() => {
    w.focus();
    w.print();
  }, 400);
}

export async function exportReportPdf(elementId: string, title: string) {
  const el = document.getElementById(elementId);
  if (!el) return;
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });
  const img = canvas.toDataURL('image/png');
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  pdf.setFontSize(14);
  pdf.text(title, margin, 12);
  const imgW = pageW - margin * 2;
  const imgH = (canvas.height * imgW) / canvas.width;
  let y = 18;
  let remaining = imgH;
  let srcY = 0;
  const pageImgH = pageH - 22;
  while (remaining > 0) {
    const sliceH = Math.min(pageImgH, remaining);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = Math.floor((sliceH / imgH) * canvas.height);
    const ctx = sliceCanvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(
        canvas,
        0,
        Math.floor(srcY),
        canvas.width,
        sliceCanvas.height,
        0,
        0,
        canvas.width,
        sliceCanvas.height
      );
    }
    pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, y, imgW, sliceH);
    remaining -= sliceH;
    srcY += (sliceH / imgH) * canvas.height;
    if (remaining > 0) {
      pdf.addPage();
      y = margin;
    }
  }
  pdf.save(`${title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
}

export function exportTablePdf(
  title: string,
  head: string[],
  body: (string | number)[][]
) {
  const pdf = new jsPDF('l', 'mm', 'a4');
  pdf.setFontSize(14);
  pdf.text(title, 14, 14);
  autoTable(pdf, {
    startY: 20,
    head: [head],
    body: body.map((r) => r.map(String)),
    styles: { fontSize: 8 },
  });
  pdf.save(`${title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
}

export function objToCsvRows(
  title: string,
  obj: Record<string, unknown>
): (string | number)[][] {
  const rows: (string | number)[][] = [[title], ['key', 'value']];
  Object.entries(obj).forEach(([k, v]) => {
    if (v != null && typeof v === 'object') {
      rows.push([k, JSON.stringify(v)]);
    } else {
      rows.push([k, v == null ? '' : (v as string | number)]);
    }
  });
  return rows;
}
